import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConnectionService } from '@services/connection-service';
import { UserService } from '@services/user/user.service';
import { AppConfig } from 'client/app/app.config';
import { Regimen } from 'client/app/app-constant-types';

const FACE_PLACEHOLDER_COUNT = 4;
const HAIR_IMAGE_CDN_BASE = 'https://cdn.cureskin.com/app/img/HairTestimonialsNew';
const FACE_IMAGE_CDN_BASE = 'https://cdn.cureskin.com/app/img/testimonials/testimonial-wall';

@Component({
  selector: 'testimonial-wall',
  templateUrl: './testimonial-wall.html',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestimonialWallComponent {
  readonly regimen = input<Regimen | undefined>(undefined);

  private readonly appConfig = inject(AppConfig);
  private readonly conn = inject(ConnectionService);
  private readonly userService = inject(UserService);

  private readonly user = computed((): Table.User | undefined => {
    this.regimen();
    return this.conn.getActingUser();
  });

  readonly concern = computed<string>(
    (): string => this.regimen()?.class || this.appConfig.Shared.Regimen.Class.FACE,
  );

  readonly language = computed<string>((): string => {
    const { EN, HI } = this.appConfig.Shared.Languages;
    return this.user()?.get('languagePreference') === HI ? HI : EN;
  });

  readonly faceImages = computed<string[]>((): string[] => {
    if (!this.isFace()) return [];
    return Array.from(
      { length: FACE_PLACEHOLDER_COUNT },
      (_, i): string => `${FACE_IMAGE_CDN_BASE}/${this.language()}_${i}.jpg`,
    );
  });

  readonly hairImageUrl = computed<string>(
    (): string => {
      const gender = this.userService.getUserGender();
      return `${HAIR_IMAGE_CDN_BASE}/${gender.toLowerCase()}-hair-${this.language()}.png`
    }
  );

  readonly isFace = computed<boolean>((): boolean => this.concern() === this.appConfig.Shared.Regimen.Class.FACE);
  readonly isHair = computed<boolean>((): boolean => this.concern() === this.appConfig.Shared.Regimen.Class.HAIR);
}
